import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { Category } from '@modules/common/types';
import { getFieldNameFromEqlField } from '@modules/common/utils';
import { FeedbackListDrawer } from '@modules/feedback/components/FeedbackListDrawer';
import { FeedbackListDrawerProps } from '@modules/feedback/components/FeedbackListDrawer/FeedbackListDrawer';
import { useGetSelectedFeedbackMetadata } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import {
  DEFAULT_COMPARE_DURATION_A_NAME,
  DEFAULT_COMPARE_DURATION_B_NAME,
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import {
  useFetchQuantifyReport,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { useGetCompareColors } from '@modules/quantify/hooks/useGetCompareColors';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { LabelUpdateInProgressIcon } from '@modules/taxonomy/components/LabelUpdateInProgressIcon';
import { getInfoMessageForLabelState } from '@modules/taxonomy/helpers';
import { CircleRounded } from '@mui/icons-material';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { LabelType } from 'generated/graphql';
import pluralize from 'pluralize';
import { FieldNames } from 'v2/core/entities/fields';

interface QuantifyFeedbackListDrawerProps
  extends Omit<FeedbackListDrawerProps, 'header'> {
  metricKeyLabel?: string;
  metricKeyId?: string;
  metricKeyCategory?: Category;
  isCompareFieldA?: boolean;
  isMetricKeyInvisible?: boolean;
  isMetricKeyUpdateInProgress?: boolean;
}

export function QuantifyFeedbackListDrawer({
  open,
  metricKeyLabel,
  metricKeyId,
  onClose,
  queryContainer,
  metricKeyCategory,
  isCompareFieldA,
  isMetricKeyInvisible,
  isMetricKeyUpdateInProgress,
}: QuantifyFeedbackListDrawerProps) {
  const theme = useTheme();

  const fetchQuantifyReport = useFetchQuantifyReport();
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const compareTo = useQuantifyStore(({ query }) => query.compareTo);
  const shouldRefetch = useFeedbackOnFeedbackStore(
    ({ shouldRefetch }) => shouldRefetch,
  );
  const compareFilterAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldAName ||
      DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldBName ||
      DEFAULT_COMPARE_FILTER_B_NAME,
  );
  const compareDurationAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareDurationField.compareDurationAName ||
      DEFAULT_COMPARE_DURATION_A_NAME,
  );
  const compareDurationBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareDurationField.compareDurationBName ||
      DEFAULT_COMPARE_DURATION_B_NAME,
  );

  const fieldName = groupBy ? getFieldNameFromEqlField(groupBy) : undefined;
  const labelType =
    fieldName === FieldNames.Reason ? LabelType.Reason : LabelType.Aspect;

  const { colorA, colorB } = useGetCompareColors();
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const isSingleRecordQuery = useIsSingleRecordQuery();

  const groupByLabel = (() => {
    if (!groupBy || isSingleRecordQuery) {
      return null;
    }
    if (metricKeyId) {
      return metricKeyLabel;
    }
    return !!groupBy.detail?.metadata
      ? null
      : `All ${pluralize(groupBy.displayName)}`;
  })();

  const getDrawerHeaderTitle = () => {
    if (compareTo) {
      const compareByLabelA =
        compareTo.type === 'whereClause'
          ? compareFilterAName
          : compareDurationAName;
      const compareByLabelB =
        compareTo.type === 'whereClause'
          ? compareFilterBName
          : compareDurationBName;

      return (
        <Typography variant="subtitle2">
          Feedback from{' '}
          <CircleRounded
            sx={{ fontSize: 14 }}
            style={{ color: isCompareFieldA ? colorA : colorB }}
          />{' '}
          {isCompareFieldA ? compareByLabelA : compareByLabelB}
        </Typography>
      );
    }

    return (
      <Typography variant="subtitle2">
        Feedback with existing filters for
      </Typography>
    );
  };

  const onDrawerClose = () => {
    if (shouldRefetch) {
      fetchQuantifyReport();
    }
    onClose();
  };

  const selectedFeedbackMetadata = useGetSelectedFeedbackMetadata({
    whereClause: queryContainer.WhereClause,
    labels:
      metricKeyId && groupBy
        ? [
            {
              id: metricKeyId,
              fieldName: getFieldNameFromEqlField(groupBy),
              name: metricKeyLabel ?? '',
            },
          ]
        : undefined,
  });

  const shouldShowCategory = !!metricKeyCategory;

  return (
    <FeedbackListDrawer
      open={open}
      onClose={onDrawerClose}
      queryContainer={queryContainer}
      selectedFeedbackMetadata={selectedFeedbackMetadata}
      header={{
        shouldEnableSort: true,
        renderTitle: getDrawerHeaderTitle,
        renderDescription: () => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {groupBy && (
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{
                  lineHeight: '20px',
                }}
              >
                {getFormattedDisplayNameForEqlField({
                  eqlField: groupBy,
                  shouldRemoveFileUploadPrefix,
                  isUnifiedFieldEnabled,
                })}
              </Typography>
            )}
            {!groupByLabel ? null : (
              <>
                {':'}
                {shouldShowCategory && (
                  <Box>
                    <CategoryIcon
                      category={metricKeyCategory}
                      size={18}
                      fill={
                        isMetricKeyInvisible
                          ? theme.palette.text.disabled
                          : undefined
                      }
                    />
                  </Box>
                )}
                <Tooltip
                  arrow
                  title={
                    isMetricKeyInvisible
                      ? getInfoMessageForLabelState({
                          labelState: 'invisible',
                          labelType: labelType,
                        })
                      : null
                  }
                  placement="right"
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      lineHeight: '20px',
                    }}
                    color={
                      isMetricKeyInvisible ? 'text.disabled' : 'text.primary'
                    }
                  >
                    {groupByLabel}
                  </Typography>
                </Tooltip>
                {isMetricKeyUpdateInProgress && (
                  <Box>
                    <LabelUpdateInProgressIcon
                      size={20}
                      labelType={labelType}
                    />
                  </Box>
                )}
              </>
            )}
          </Stack>
        ),
      }}
    />
  );
}
